import { RequestRegistry } from './request-registry';
import { UserService } from '../user/user.service';
import {ControllerQuery} from "./controller-query";
import {ControllerRequestModel} from "./controller-request.model";
import {UtilService} from "../util/util.service";
import {HttpClient} from "@angular/common/http";
import {FeatureFlagsService} from "../feature-flag/feature-flags.service";
import {EventService} from "../event/event.service";

// We really don't want to use jQuery on the Angular side
declare var $: any;

export class ControllerRequest {

  private _url: string = "../services/controller";
  private _httpMethod: string = 'POST';
  private _authenticated: boolean;
  private _controller: string;
  private _method: string;
  private _tile: string;
  private _tileID: string;
  private _view: string;
  private _viewID: string;
  private _aspect: string;
  private _data: any;
  private _query = new ControllerQuery();
  private _successFunc: any;
  private _saveAsFileName: string;
  private _saveAsFileType: string;
  private _saveAsResultPath: string;
  private _errorFunc: any = this.defaultErrorResponseHandler;
  private _multiPartFormId: string;
  private _table: any;
  private _viewModel: any;
  private _relationships: string[];
  private _async: boolean = true;
  private _metric: any;

  constructor(private requestRegistry: RequestRegistry,
              private userService: UserService,
              private utilService: UtilService,
              private http: HttpClient,
              private featureFlagsService: FeatureFlagsService,
              private eventService: EventService) {
    this._authenticated = userService.isAuthenticated();
    this.koViewModelErrorConsoleClearErrors();
  }

  url(url: string): ControllerRequest {
    this._url = url;
    return this;
  }

  getUrl(): String {
    return this._url;
  }

  multiPartForm(multiPartFormId: string) {
    this._multiPartFormId = multiPartFormId;
    return this;
  }

  anon(anon: boolean): ControllerRequest {
    this._url = anon ? "../anonservices/controller" : "../services/controller";
    return this;
  }

  async(async: boolean): ControllerRequest {
    this._async = async;
    return this;
  }

  saveAs(fileName: string, fileType: string, resultPath: string): ControllerRequest {
    this._saveAsFileName = fileName;
    this._saveAsFileType = fileType;
    this._saveAsResultPath = resultPath;
    return this;
  }

  successFunc(successFunc: any): ControllerRequest {
    this._successFunc = successFunc;
    return this;
  }

  errorFunc(errorFunc: any): ControllerRequest {
    this._errorFunc = errorFunc;
    return this;
  }

  controller(controller: string): ControllerRequest {
    this._controller = controller;
    return this;
  }

  method(method: string): ControllerRequest {
    this._method = method;
    return this;
  }

  tile(tile: string): ControllerRequest {
    this._tile = tile;
    return this;
  }

  tileID(tileID: string) {
    this._tileID = tileID;
    return this;
  }

  httpMethod(httpMethod): ControllerRequest {
    this._httpMethod = httpMethod;
    return this;
  }

  getControllerQuery(): ControllerQuery {
    return this._query;
  }

  controllerQuery(query: ControllerQuery): ControllerRequest {
    this._query = query;
    return this;
  }

  data(data: any): ControllerRequest {
    this._data = data;
    return this;
  }

  getData(): any {
    return this._data;
  }

  table(table: string): ControllerRequest {
    this._table = table;
    return this;
  }

  view(view: any): ControllerRequest {
    this._view = view;
    return this;
  }

  viewID(viewID: string): ControllerRequest {
    this._viewID = viewID;
    return this;
  }

  aspect(aspect: string): ControllerRequest {
    this._aspect = aspect;
    return this;
  }

  viewModel(viewModel: any): ControllerRequest {
    this._viewModel = viewModel;
    return this;
  }

  relationships(relationships: string[] = ["*"]): ControllerRequest {
    this._relationships = relationships;
    return this;
  }

  getHttpMethod(): string {
    if (!this._httpMethod) {
      this._httpMethod = "POST";
    }
    return this._httpMethod;
  }

  execute() {
    const httpMethod = this.getHttpMethod();

    let requestPayload: ControllerRequestModel | string = this.generatePayload();

    // Register a metric for the current request
    this._metric = this.koPerformanceMetrics().createSingleMetric("controller-requests.ts", {
      controller: requestPayload.controller,
      method: requestPayload.method,
      table: requestPayload.table
    });

    if (!this._multiPartFormId) {
      requestPayload = JSON.stringify(requestPayload);
    }

    // Declared ahead of time to allow it to be logged in the request and response.
    let requestConfig: any = {};

    const generateSuccessFuncWrapper = (callback: any) => {
      return (data: any, status: any, response: any) => {
        if (this.featureFlagsService.isOn('controller-request-display')) {
          console.log("controller-request: success response", requestConfig, data);
        }
        const requestId = response.getResponseHeader("x-servicely-request-id");

        this.requestRegistry.responseReceived(requestId);

        if (data.tableDefinition != null) {
          this.koTableRegistryRegisterTableDefinition(data.tableDefinition);
        } else if (data.loadTableDefinition != null) {
          data.tableDefinition = (window as any).TableManager.getRawTableDefinition(data.loadTableDefinition);
        }

        // Log the metric
        this.koPerformanceMetrics().metricSuccess(this._metric, response);

        if (this._saveAsFileName) {
          const fileType = this._saveAsFileType || "text/plain;charset=utf-8";
          const contentPath = this._saveAsResultPath || "data";
          const fileContent = UtilService.dot(data, contentPath, "");
          const blob = new Blob([ fileContent ], { type: fileType });
          this.koFileSaverSaveAs(blob, this._saveAsFileName);
        }

        callback(data, status, response);
      };
    };

    const generateErrorFuncWrapper = (callback: any) => {
      return (response: any, textStatus: string, errorThrown: any) => {
        // Log the metric
        this.koPerformanceMetrics().metricError(this._metric, response);

        // Note: the 'x-servicely-request-id' header does not always (if ever) get returned for errors.
        if (this.featureFlagsService.isOn('controller-request-display')) {
          console.log("controller-request: error response", requestConfig, response, textStatus, errorThrown);
        }
        this.requestRegistry.responseReceived(response.getResponseHeader('x-servicely-request-id') || requestID);

        callback(response, textStatus, errorThrown);
      };
    };

    const requestID = `${new Date().getTime()}`;
    this.requestRegistry.requestSend(requestID);

    const headers: any = {
      "x-servicely-request-id" : requestID
    };

    try {
      headers["x-servicely-location"] = window.location.href;
    } catch (e) {}

    const successFuncWrapper = generateSuccessFuncWrapper((data: any, status: any, response: any) => {
        if (this._successFunc) {
          this._successFunc(data, status, response);
        }
    });

    const errorFuncWrapper = generateErrorFuncWrapper((response: any, textStatus: string, errorThrown: any) => {
        if (this._errorFunc) {
          this._errorFunc(response, textStatus, errorThrown);
        }
    });

    requestConfig  = {
      type: httpMethod,
      url: this._url,
      // NB: Required for posts, otherwise the Camel HTTPBinder complains.
      contentType: 'application/json',
      dataType: 'json',
      data: requestPayload,
      success: successFuncWrapper,
      error: errorFuncWrapper,
      async: this._async,
      headers: headers
    };

    this.eventService.requestStarted();

    const sendRequest = () => {
      // Use jQuery for now but should replace with Angular httpClient
      if (this._multiPartFormId) {
        $("#" + this._multiPartFormId).ajaxSubmit(requestConfig);
      } else {
        $.ajax(requestConfig);
      }
    };

    // Return a promise if _successFunc is not defined. This is for async/await usage.
    if (!this._successFunc) {
      return new Promise((resolve, reject) => {

          const promiseSuccessWrapper = generateSuccessFuncWrapper((data: any, status: any, response: any) => {
            resolve(response);
          });

          const promiseErrorWrapper = generateErrorFuncWrapper((response: any, textStatus: string, errorThrown: any) => {
            this.defaultErrorResponseHandler(response, textStatus, errorThrown);
            reject(response);
          });

          requestConfig.success = promiseSuccessWrapper;
          requestConfig.error = promiseErrorWrapper;

          sendRequest();
        }
      );
    } else {
      sendRequest();
    }
  }

  private generatePayload(): ControllerRequestModel {

    let model: ControllerRequestModel = {
      authenticated: this._authenticated,
      query: this._query.toQueryModel()
    };

    if (this._controller) {
      model.controller = this._controller;
    }
    if (this._method) {
      model.method = this._method;
    }
    if (this._table) {
      model.table = this._table;
    }
    if (this._view) {
      model.view = this._view;
    }
    if (this._viewID) {
      model.viewID = this._viewID;
    }
    if (this._aspect) {
      model.aspect = this._aspect;
    }
    if (this._tile) {
      model.tile = this._tile;
    }
    if (this._tileID) {
      model.tileID = this._tileID;
    }
    if (this._data) {
      model.data = this._data;
    }
    if (this._relationships) {
      model.relationships = this._relationships.join(",");
    }
    if (this._async) {
      model.async = this._async;
    }

    return model;
  }

  private defaultErrorResponseHandler(response: any, textStatus: string, errorThrown: any) {
    console.error("controller-request.ts: defaultErrorResponseHandler", response, textStatus, errorThrown);

    let data: any = "";
    let noResponse = false;

    try {
        if (response.responseText == null || response.responseText == "") {
          noResponse = true;
        }

        data = JSON.parse(response.responseText);
      } catch (e) {
        data = "";
    }

    if (response.status === 401 && (noResponse || data.loginRequired)) {
      const anonymousPages = ["/login", "/ForgotPassword", "/ResetPassword", "/2FA"];
      const anon = anonymousPages.includes(window.location.pathname);

      if (!anon) {
        (window as any).Navigate.showLoggedOutInfo();
      }
      return;

    } else {

      // Display errors
      if (data.errors && data.errors.length > 0) {
        if (response.status === 500) {
          this.koViewModelErrorConsoleSetErrors([this.koI18nGetMessage("invalid.request")]);
        } else {
          data.errors.splice(0, 0, this.koI18nGetMessage("exception.encountered"));
          this.koViewModelErrorConsoleSetErrors(data.errors);
        }
      }

      // Display validation errors
      if (data.validationErrors) {
        if (this.featureFlagsService.isOn('controller-request-display')) {
          console.log("controller-request validationErrors", data.validationErrors);
        }
        this.koHandleValidationErrors(data);
      }

    }
  }

  private koHandleValidationErrors(data: any) {
    const koTableRecordAPI: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.tableRecordAPI;
    const koViewModels: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.viewmodels;
    if (this._viewModel) {
      let tableRecord = koTableRecordAPI.getTableRecord(this._viewModel.getTableName(), this._viewModel.data);
      if (tableRecord.getTableName() === data.tableName) {
        data.validationErrors.forEach((e) => {
          tableRecord.getField(e.fieldName).setFieldValidationError(e.errorString);
        });
      } else {
        throw new Error(`Table name of view model '${tableRecord.getTableName()}' does not match the table returned from the server ' ${data.tableName}'`);
      }
    } else {
      data.validationErrors.forEach((e) => {
        koViewModels.errorConsole.setErrors([e.errorString]);
      });
    }
  }

  private koFileSaverSaveAs(blob: any, filename: string) {
    const koFileSaverSaveAs: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.saveAs;
    koFileSaverSaveAs(blob, filename);
  }

  private koTableRegistryRegisterTableDefinition(tableDefinition: any) {
    const koTableManager: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.tableManager;
    koTableManager.registerTableDefinition(tableDefinition);
  }

  private koViewModelErrorConsoleClearErrors() {
    const koViewModels: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.viewmodels;
    koViewModels.errorConsole.clearErrors();
  }

  private koViewModelErrorConsoleSetErrors(errors: string[]) {
    const koViewModels: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.viewmodels;
    koViewModels.errorConsole.setErrors(errors);
  }

  private koI18nGetMessage(i18n: string): string {
    const koI18n: any = (window as any).KO_REQUEST_FACTORY_BRIDGE.i18n;
    return koI18n.getMessage(i18n);
  }

  private koPerformanceMetrics(): any {
    return (window as any).KO_REQUEST_FACTORY_BRIDGE.performanceMetrics;
  }
}
